import {
  ContentBlock,
  ContentState,
  EditorState,
  RawDraftContentState,
  convertFromRaw,
  convertToRaw,
} from 'draft-js';
import _draftToHtml from 'draftjs-to-html';
import htmlToDraftImport from 'html-to-draftjs';

import { cjsDefault } from './cjs-default';

const _htmlToDraft = cjsDefault(htmlToDraftImport);

export function draftToHtml(content: RawDraftContentState) {
  return _draftToHtml(content, undefined, undefined, (entity, text) => {
    if (entity.type !== 'LINK') return;
    return `<a href="${entity.data.url}" target="_blank">${text}</a>`;
  });
}

export function htmlToDraft(content: string): ContentState {
  const { contentBlocks, entityMap } = _htmlToDraft(content);
  return ContentState.createFromBlockArray(contentBlocks, entityMap);
}

export function tryParseHtmlToEditorState(value: string): EditorState | null {
  try {
    let contentState = htmlToDraft(value);
    if (contentState) {
      let editorState = EditorState.createWithContent(contentState);
      return EditorState.moveSelectionToEnd(editorState);
    }
  } catch (e) {}
  return null;
}

export function tryParseJsonToEditorState(value: string): EditorState | null {
  try {
    let data = JSON.parse(value);
    let editorState = EditorState.createWithContent(convertFromRaw(data));
    return EditorState.moveSelectionToEnd(editorState);
  } catch (e) {}
  return null;
}

export function editorStateToHtml(editorState: EditorState) {
  return draftToHtml(convertToRaw(editorState.getCurrentContent()));
}

export function isSameEditorState(first: string, second: string) {
  if (!first || !second) {
    return first === second;
  }

  // @PERF check if we can do it without converting to strings at the end
  // This is disgusting but the text-editor can have some invisible characters
  // and newlines so we need to construct a new clean editorstate to compare.
  const prevContentState = htmlToDraft(first);
  const prevEditorState = EditorState.createWithContent(prevContentState);
  const currContentState = htmlToDraft(second);
  const currEditorState = EditorState.createWithContent(currContentState);
  const prevStr = draftToHtml(convertToRaw(prevEditorState.getCurrentContent()));
  const currStr = draftToHtml(convertToRaw(currEditorState.getCurrentContent()));
  return prevStr === currStr;
}

export function isEditorEmpty(htmlOrEditorState: string | EditorState) {
  if (!htmlOrEditorState) return true;

  if (typeof htmlOrEditorState === 'string') {
    const contentState = htmlToDraft(htmlOrEditorState);
    const blocks = contentState.getBlocksAsArray();
    return blocks.length === 1 && blocks[0].getText() === '';
  }

  const raw = convertToRaw(htmlOrEditorState.getCurrentContent());
  return raw.blocks.length === 1 && raw.blocks[0].text === '';
}

export function isEditorHtmlEmpty(html: string) {
  if (!html) return true;
  const contentState = htmlToDraft(html);
  const blocks = contentState.getBlocksAsArray();
  return blocks.length === 1 && blocks[0].getText() === '';
}

export function makeEditorState(value: string | EditorState): EditorState {
  if (!value) return EditorState.createEmpty();
  if (typeof value === 'object') return value;

  return (
    tryParseJsonToEditorState(value) ||
    tryParseHtmlToEditorState(value) ||
    EditorState.createEmpty()
  );
}

/**
 * If all currently selected blocks are of same type the function will return their type,
 * Else it will return empty string.
 */
export function getSelectedBlocksType(editorState: EditorState) {
  const blocks = getSelectedBlocksList(editorState);
  const hasMultipleBlockTypes = blocks.some(
    (block) => block?.getType() !== blocks?.get(0)?.getType(),
  );
  if (!hasMultipleBlockTypes) {
    return blocks?.get(0)?.getType();
  }
  return undefined;
}

/**
 * Function returns collection of currently selected blocks.
 */
function getSelectedBlocksMap(editorState: EditorState) {
  const selectionState = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const startKey = selectionState.getStartKey();
  const endKey = selectionState.getEndKey();
  const blockMap = contentState.getBlockMap();
  return blockMap
    .toSeq()
    .skipUntil((_, k) => k === startKey)
    .takeUntil((_, k) => k === endKey)
    .concat([[endKey, blockMap.get(endKey)]]);
}

/**
 * Function returns collection of currently selected blocks.
 */
function getSelectedBlocksList(editorState: EditorState) {
  return getSelectedBlocksMap(editorState).toList();
}

/**
 * Function returns the first selected block.
 */
export function getSelectedBlock(editorState: EditorState) {
  if (editorState) {
    return getSelectedBlocksList(editorState).get(0);
  }
  return undefined;
}

export function findEntitiesByType(entityType: string) {
  return function findEntity(
    contentBlock: ContentBlock,
    callback: any,
    contentState: ContentState,
  ) {
    contentBlock.findEntityRanges((character) => {
      const entityKey = character.getEntity();
      return entityKey !== null && contentState.getEntity(entityKey).getType() === entityType;
    }, callback);
  };
}
